import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  X, ClipboardList, Plus, Trash2, Loader2, Upload, Download,
  FileSpreadsheet, Image, AlertTriangle, ChevronDown, Search,
  Sparkles, CheckCircle2,
} from 'lucide-react';
import toast from 'react-hot-toast';
import purchaseOrderService from '../../../../services/purchaseOrderService';
import { inventoryitemService } from '../../../../services/inventoryitemService';
import useTax from '../../../../hooks/useTax';

const TABS = [
  { id: 'manual',  label: 'Manual Entry',  icon: ClipboardList },
  { id: 'excel',   label: 'Excel Upload',  icon: FileSpreadsheet },
  { id: 'image',   label: 'Image Upload',  icon: Image },
];

// AI-match confidence → badge styling
const CONFIDENCE_STYLES = {
  HIGH:   'bg-green-100 text-green-700',
  MEDIUM: 'bg-amber-100 text-amber-700',
  LOW:    'bg-orange-100 text-orange-700',
  NONE:   'bg-slate-100 text-slate-500',
};

// Round money to 2 decimals (avoids float drift so UI totals match the payload)
const r2 = (n) => Math.round((Number(n) + Number.EPSILON) * 100) / 100;

const emptyLine = () => ({
  _key: Date.now() + Math.random(),
  inventoryItemId: '',
  itemName: '',
  quantity: '',
  uom: '',
  unitPrice: '',
  totalPrice: 0,
  taxId: null, // default filled once taxes load / on item select
});

// Empty "other charge" row (taxId defaults to the default tax once taxes load)
const emptyCharge = () => ({
  _key: Date.now() + Math.random(),
  name: '',
  amount: '',
  taxId: null,
});

// Normalise inventory items regardless of API response shape
const normaliseInventoryItems = (response) => {
  const arr = Array.isArray(response?.data)    ? response.data    :
              Array.isArray(response?.content)  ? response.content :
              Array.isArray(response)            ? response         : [];
  return arr.map((item) => ({
    id:       item.id,
    name:     item.name,
    // handle multiple possible UOM field shapes
    uom:      item.uom?.symbol
           ?? item.unitOfMeasurement?.symbol
           ?? item.idUom?.symbol
           ?? item.uomSymbol
           ?? 'kg',
    unitCost: item.unitCost ?? item.costPrice ?? 0,
    // Tax saved on the inventory item — supports flat taxId or nested relation shapes
    taxId:    (item.taxId ?? item.idTax?.id ?? item.tax?.id) || null,
  }));
};

export default function PurchaseOrderForm({ po, suppliers = [], onClose, onSave }) {
  const isEdit = !!po;

  // Prevent the mouse wheel from changing number-input values (keeps page scroll)
  const preventWheelChange = (e) => e.currentTarget.blur();

  // ── Tax (same source/hook as the Menu form) ─────────────────────────────────────
  const { taxes } = useTax();
  const activeTaxes  = (taxes || []).filter((t) => t.isActive);
  const defaultTax   = activeTaxes.find((t) => t.isDefault ?? t.is_default) || null;
  const defaultTaxId = defaultTax ? defaultTax.id : null;
  // Resolve a tax rate (%) from a tax id
  const taxRateById  = (id) => {
    if (id == null || id === '') return 0;
    const t = activeTaxes.find((x) => String(x.id) === String(id));
    return t ? Number(t.rate) || 0 : 0;
  };

  // ── Form state ────────────────────────────────────────────────────────────────
  const [supplierId,           setSupplierId]           = useState(po?.supplierId || '');
  const [orderDate,            setOrderDate]            = useState(po?.orderDate || new Date().toISOString().split('T')[0]);
  const [expectedDeliveryDate, setExpectedDeliveryDate] = useState(po?.expectedDeliveryDate || '');
  const [notes,                setNotes]                = useState(po?.notes || '');
  const [discount,             setDiscount]             = useState(po?.discountAmount != null ? String(po.discountAmount) : '');
  const [discountType,         setDiscountType]         = useState('FLAT'); // 'FLAT' | 'PERCENT'
  const [lineItems,            setLineItems]            = useState(
    po?.lineItems?.map((li) => ({ ...li, _key: li.id || Date.now() + Math.random() })) || [emptyLine()]
  );

  // ── Other charges (dynamic) ─────────────────────────────────────────────────────
  const initialCharges = Array.isArray(po?.extraChargesDetails) && po.extraChargesDetails.length > 0
    ? po.extraChargesDetails.map((c) => ({
        _key:   Date.now() + Math.random(),
        name:   c.description ?? c.key ?? c.name ?? '',
        amount: c.amount != null ? String(c.amount) : '',
        taxId:  c.taxId ?? c.idTax?.id ?? null,
      }))
    : [];
  const [showOtherCharges, setShowOtherCharges] = useState(initialCharges.length > 0);
  const [otherCharges,     setOtherCharges]     = useState(initialCharges);

  const [activeTab, setActiveTab] = useState('manual');
  const [saving,    setSaving]    = useState(false);
  const [errors,    setErrors]    = useState({});

  // ── Inventory items ───────────────────────────────────────────────────────────
  const [inventoryItems, setInventoryItems] = useState([]);
  const [loadingItems,   setLoadingItems]   = useState(false);

  // ── Excel upload ──────────────────────────────────────────────────────────────
  const [excelFile,      setExcelFile]      = useState(null);
  const [excelParsed,    setExcelParsed]    = useState(null);
  const [uploadingExcel, setUploadingExcel] = useState(false);
  const excelInputRef = useRef(null);

  // ── Image upload ──────────────────────────────────────────────────────────────
  const [imageFile,    setImageFile]    = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const imageInputRef = useRef(null);

  // ── AI extraction + review ──────────────────────────────────────────────────────
  const [extracting,       setExtracting]       = useState(false);
  const [extraction,       setExtraction]       = useState(null); // raw result from /extract
  const [reviewItems,      setReviewItems]      = useState([]);    // editable review rows
  const [reviewSupplierId, setReviewSupplierId] = useState('');

  // ── Supplier dropdown ─────────────────────────────────────────────────────────
  const [showSupplierDropdown, setShowSupplierDropdown] = useState(false);
  const [supplierSearch,       setSupplierSearch]       = useState('');
  const supplierDropdownRef = useRef(null);

  // ── Item dropdown per row ─────────────────────────────────────────────────────
  const [openItemDropdown, setOpenItemDropdown] = useState(null);
  const [itemSearch,       setItemSearch]       = useState('');
  const [dropdownPos,      setDropdownPos]      = useState(null);
  const itemDropdownRef = useRef(null);

  // ── Load inventory items ──────────────────────────────────────────────────────
  useEffect(() => {
    setLoadingItems(true);
    inventoryitemService
      .getAll()
      .then((data) => setInventoryItems(normaliseInventoryItems(data)))
      .catch(() => toast.error('Failed to load inventory items'))
      .finally(() => setLoadingItems(false));
  }, []);

  // Once taxes load, auto-select the default tax (isDefault = true) on line items
  // that don't yet have one. Rows with their own tax (item's taxId, edit data, or a
  // user choice) are left as-is. Other Charges are intentionally NOT touched — they
  // default to "No Tax".
  useEffect(() => {
    if (defaultTaxId == null) return;
    setLineItems((prev) =>
      prev.map((li) => (li.taxId == null ? { ...li, taxId: defaultTaxId } : li))
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultTaxId]);

  // ── Click-outside for dropdowns ───────────────────────────────────────────────
  useEffect(() => {
    const handler = (e) => {
      if (supplierDropdownRef.current && !supplierDropdownRef.current.contains(e.target)) {
        setShowSupplierDropdown(false);
      }
      if (itemDropdownRef.current && !itemDropdownRef.current.contains(e.target)) {
        setOpenItemDropdown(null);
        setDropdownPos(null);
        setItemSearch('');
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const filteredSuppliers = useMemo(() => {
    if (!supplierSearch) return suppliers;
    const q = supplierSearch.toLowerCase();
    return suppliers.filter((s) => s.name.toLowerCase().includes(q));
  }, [suppliers, supplierSearch]);

  const filteredInventoryItems = useMemo(() => {
    if (!itemSearch) return inventoryItems;
    const q = itemSearch.toLowerCase();
    return inventoryItems.filter((it) => it.name.toLowerCase().includes(q));
  }, [inventoryItems, itemSearch]);

  // ── Line item handlers ────────────────────────────────────────────────────────
  const updateLine = (idx, field, value) => {
    setLineItems((prev) => {
      const updated = [...prev];
      updated[idx] = { ...updated[idx], [field]: value };
      if (field === 'quantity' || field === 'unitPrice') {
        const qty   = parseFloat(field === 'quantity'  ? value : updated[idx].quantity)  || 0;
        const price = parseFloat(field === 'unitPrice' ? value : updated[idx].unitPrice) || 0;
        updated[idx].totalPrice = qty * price;
      }
      return updated;
    });
  };

  const selectItem = (idx, item) => {
    // Item tax rule:
    //   if (inventoryItem.taxId != null)  → use the item's own tax
    //   else                              → fall back to the default tax (isDefault = true)
    const itemHasTax = item.taxId !== null && item.taxId !== undefined && item.taxId !== '';
    const resolvedTaxId = itemHasTax ? item.taxId : (defaultTaxId ?? null);
    setLineItems((prev) => {
      const updated = [...prev];
      updated[idx] = {
        ...updated[idx],
        inventoryItemId: item.id,
        itemName:  item.name,
        uom:       item.uom,
        unitPrice: item.unitCost || updated[idx].unitPrice,
        totalPrice: (parseFloat(updated[idx].quantity) || 0) *
                    (item.unitCost || parseFloat(updated[idx].unitPrice) || 0),
        taxId: resolvedTaxId,
      };
      return updated;
    });
    setOpenItemDropdown(null);
    setDropdownPos(null);
    setItemSearch('');
  };

  const addLine    = () => setLineItems((prev) => [...prev, { ...emptyLine(), taxId: defaultTaxId ?? null }]);
  const removeLine = (idx) => setLineItems((prev) => prev.filter((_, i) => i !== idx));

  // ── Other charges handlers ──────────────────────────────────────────────────────
  // New charge rows default to "No Tax" (null). The default GST is NOT auto-applied here.
  const makeCharge = () => ({ ...emptyCharge(), taxId: null });
  const openOtherCharges = () => {
    setShowOtherCharges(true);
    setOtherCharges((prev) => (prev.length ? prev : [makeCharge()]));
  };
  const addCharge = () => setOtherCharges((prev) => [...prev, makeCharge()]);
  const updateCharge = (idx, field, value) => {
    setOtherCharges((prev) => {
      const updated = [...prev];
      updated[idx] = { ...updated[idx], [field]: value };
      return updated;
    });
  };
  const removeCharge = (idx) => {
    setOtherCharges((prev) => {
      const next = prev.filter((_, i) => i !== idx);
      if (next.length === 0) setShowOtherCharges(false);
      return next;
    });
  };

  // ── Totals ────────────────────────────────────────────────────────────────────
  // Tax is EXCLUSIVE and applied per row.
  //   Line:   amount = qty × price · taxAmount = amount × rate% · total = amount + taxAmount
  //   Charge: amount (entered) · taxAmount = amount × rate% · total = amount + taxAmount
  // Grand Total = Subtotal(items, pre-tax) + Item Tax Total + Other Charges Total(incl tax) − Discount
  const lineRows = lineItems.map((li) => {
    const amount    = li.totalPrice || 0;               // qty × unit price (pre-tax)
    const taxAmount = r2(amount * (taxRateById(li.taxId) / 100));
    return { amount, taxAmount, total: r2(amount + taxAmount) };
  });
  const chargeRows = otherCharges.map((c) => {
    const amount    = parseFloat(c.amount) || 0;        // entered charge (pre-tax)
    const taxAmount = r2(amount * (taxRateById(c.taxId) / 100));
    return {
      _key: c._key, name: c.name, taxId: c.taxId,
      amount, taxAmount, total: r2(amount + taxAmount),
    };
  });

  const subtotal        = r2(lineRows.reduce((s, x) => s + x.amount, 0));      // items, pre-tax
  const itemTaxTotal    = r2(lineRows.reduce((s, x) => s + x.taxAmount, 0));   // item tax only
  const extraCharges    = r2(chargeRows.reduce((s, x) => s + x.total, 0));     // charges, incl tax

  // Discount — flat ₹ amount or % of the (pre-tax) subtotal; only the final amount is used/saved.
  const discountInput   = Math.max(parseFloat(discount) || 0, 0);
  const discountAmount  = Math.min(
    discountType === 'PERCENT' ? r2(subtotal * (Math.min(discountInput, 100) / 100)) : discountInput,
    subtotal,
  );

  const grandTotal      = r2(subtotal + itemTaxTotal + extraCharges - discountAmount);
  const namedCharges    = chargeRows.filter((c) => c.name.trim() !== '' || c.amount !== 0);

  // ── Validation ────────────────────────────────────────────────────────────────
  const validate = () => {
    const errs = {};
    if (!supplierId)           errs.supplier = 'Supplier is required';
    if (lineItems.length === 0) errs.items   = 'At least one line item is required';
    lineItems.forEach((li, idx) => {
      if (!li.itemName && !li.inventoryItemId) errs[`item_${idx}`]  = 'Item required';
      if (!li.quantity  || Number(li.quantity)  <= 0) errs[`qty_${idx}`]   = 'Qty > 0';
      if (!li.unitPrice || Number(li.unitPrice) <= 0) errs[`price_${idx}`] = 'Price > 0';
    });
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  // ── Save ──────────────────────────────────────────────────────────────────────
  const handleSave = async (status = 'DRAFT') => {
    if (!validate()) {
      toast.error('Please Fill all required fields and fix errors');
      return;
    }
    setSaving(true);
    try {
      // Full breakdown per charge: description, amount (pre-tax), taxAmount, totalAmount
      const extraChargesDetails = chargeRows
        .filter((c) => c.name.trim() !== '' || c.amount !== 0)
        .map((c) => ({
          description: c.name.trim(),
          amount:      c.amount,
          taxAmount:   c.taxAmount,
          totalAmount: c.total,
          taxId:       c.taxId ? Number(c.taxId) : null, // for edit round-trip
        }));

      const payload = {
  supplierId:           Number(supplierId),
  orderDate,
  expectedDeliveryDate: expectedDeliveryDate || null,
  notes:                notes || '',
  status,
  inputMethod:          'MANUAL',
  discountAmount:       discountAmount,
  extraCharges:         extraCharges, // sum of every charge's totalAmount (incl tax)
  extraChargesDetails:  extraChargesDetails,
  items: lineItems.map((li, idx) => ({
    ...(li.id ? { id: li.id } : {}),
    inventoryItemId: li.inventoryItemId || null,
    itemName:        li.itemName,
    quantity:        Number(li.quantity),
    uom:             li.uom || '',
    unitPrice:       Number(li.unitPrice),
    totalPrice:      lineRows[idx].amount,     // pre-tax line amount
    taxId:           li.taxId ? Number(li.taxId) : null,
    taxAmount:       lineRows[idx].taxAmount,  // calculated exclusive tax
    notes:           '',
  })),
};

      if (isEdit) {
        await purchaseOrderService.update(po.id, payload);
        toast.success('Purchase order updated');
      } else {
        await purchaseOrderService.create(payload);
        toast.success(status === 'PENDING' ? 'Purchase order submitted' : 'Draft saved');
      }
      onSave();
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || 'Failed to save purchase order';
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  // ── Excel upload ──────────────────────────────────────────────────────────────
  const handleExcelDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer?.files?.[0] || e.target?.files?.[0];
    if (!file) return;
    const ext = file.name.split('.').pop().toLowerCase();
    if (!['xlsx', 'xls', 'csv'].includes(ext)) {
      toast.error('Please upload an .xlsx, .xls, or .csv file');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File size must be under 5MB');
      return;
    }
    setExcelFile(file);
    handleExcelUpload(file);
  };

  const handleExcelUpload = async (file) => {
    setUploadingExcel(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const result = await purchaseOrderService.uploadExcel(formData);
      // Handle both { items } and { data: { items } } shapes
      const items = result?.items ?? result?.data?.items ?? [];
      setExcelParsed(items);
      toast.success(`${items.length} items parsed from file`);
    } catch (err) {
      const msg = err?.response?.data?.message || 'Failed to parse file';
      toast.error(msg);
    } finally {
      setUploadingExcel(false);
    }
  };

  const importExcelItems = () => {
    if (!excelParsed?.length) return;
    const newLines = excelParsed.map((item) => ({
      _key:            Date.now() + Math.random(),
      inventoryItemId: '',
      itemName:        item.itemName  ?? item.name ?? '',
      quantity:        item.quantity  ?? '',
      uom:             item.uom       ?? 'kg',
      unitPrice:       item.unitPrice ?? '',
      totalPrice:      (item.quantity || 0) * (item.unitPrice || 0),
      taxId:           defaultTaxId ?? null,
    }));
    setLineItems((prev) => {
      const nonEmpty = prev.filter((li) => li.itemName || li.inventoryItemId);
      return [...nonEmpty, ...newLines];
    });
    setActiveTab('manual');
    toast.success('Items imported — review in Manual Entry tab');
  };

  // ── Image upload ──────────────────────────────────────────────────────────────
  const handleImageDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer?.files?.[0] || e.target?.files?.[0];
    if (!file) return;
    const ext = file.name.split('.').pop().toLowerCase();
    if (!['jpg', 'jpeg', 'png', 'pdf'].includes(ext)) {
      toast.error('Please upload a .jpg, .png, or .pdf file');
      return;
    }
    setImageFile(file);
    if (['jpg', 'jpeg', 'png'].includes(ext)) {
      const reader = new FileReader();
      reader.onload = (ev) => setImagePreview(ev.target.result);
      reader.readAsDataURL(file);
    } else {
      setImagePreview(null);
    }
  };

  // ── AI extraction ───────────────────────────────────────────────────────────────
  const clearImage = () => {
    setImageFile(null);
    setImagePreview(null);
    setExtraction(null);
    setReviewItems([]);
    setReviewSupplierId('');
  };

  const handleExtract = async () => {
    if (!imageFile) return;
    setExtracting(true);
    try {
      const formData = new FormData();
      formData.append('file', imageFile);
      const result = await purchaseOrderService.extractFromImage(formData); // interceptor returns body
      setExtraction(result || {});
      setReviewSupplierId(result?.matchedSupplierId ? String(result.matchedSupplierId) : '');
      setReviewItems(
        (result?.items || []).map((it) => ({
          _key:                  Date.now() + Math.random(),
          itemName:              it.itemName ?? '',
          quantity:              it.quantity ?? '',
          uom:                   it.uom ?? '',
          unitPrice:             it.unitPrice ?? '',
          matchConfidence:       it.matchConfidence || 'NONE',
          matches:               it.matches || [],
          chosenInventoryItemId: it.matchedInventoryItemId ? String(it.matchedInventoryItemId) : '',
        })),
      );
      const count = result?.items?.length || 0;
      toast.success(count ? `Extracted ${count} item(s) — review below` : 'No items detected — try a clearer image');
    } catch (err) {
      const msg = err?.response?.data?.message || 'AI extraction failed. Please enter items manually.';
      toast.error(msg);
    } finally {
      setExtracting(false);
    }
  };

  const updateReviewItem = (idx, field, value) => {
    setReviewItems((prev) => {
      const updated = [...prev];
      updated[idx] = { ...updated[idx], [field]: value };
      return updated;
    });
  };

  const importExtracted = () => {
    if (!reviewItems.length) {
      toast.error('Nothing to import');
      return;
    }
    const newLines = reviewItems.map((ri) => {
      const matched = (ri.matches || []).find((m) => String(m.id) === String(ri.chosenInventoryItemId));
      const inv = inventoryItems.find((i) => String(i.id) === String(ri.chosenInventoryItemId));
      const qty = parseFloat(ri.quantity) || 0;
      const price = parseFloat(ri.unitPrice) || 0;
      return {
        _key:            Date.now() + Math.random(),
        inventoryItemId: ri.chosenInventoryItemId || '',
        itemName:        inv?.name || matched?.name || ri.itemName,
        quantity:        ri.quantity,
        uom:             inv?.uom || ri.uom || 'kg',
        unitPrice:       ri.unitPrice,
        totalPrice:      qty * price,
        taxId:           inv?.taxId ?? defaultTaxId ?? null,
      };
    });
    setLineItems((prev) => {
      const nonEmpty = prev.filter((li) => li.itemName || li.inventoryItemId);
      return [...nonEmpty, ...newLines];
    });
    if (reviewSupplierId) setSupplierId(Number(reviewSupplierId));
    if (extraction?.orderDate) setOrderDate(extraction.orderDate);
    if (extraction?.notes) setNotes((n) => n || extraction.notes);
    setActiveTab('manual');
    toast.success('Imported — review & submit in the Manual Entry tab');
  };

  const selectedSupplier = suppliers.find((s) => s.id === Number(supplierId));

  // ── Render ────────────────────────────────────────────────────────────────────
  return (
    <div className="po-form-root fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      {/* Hide the native number-input spinner arrows within this form (Chrome/Safari/Firefox) */}
      <style>{`
        .po-form-root input[type=number]::-webkit-outer-spin-button,
        .po-form-root input[type=number]::-webkit-inner-spin-button {
          -webkit-appearance: none;
          margin: 0;
        }
        .po-form-root input[type=number] {
          -moz-appearance: textfield;
          appearance: textfield;
        }
      `}</style>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[85vh] flex flex-col overflow-hidden">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 bg-slate-50">
          <div className="flex items-center gap-2">
            <ClipboardList className="w-5 h-5 text-teal-600" />
            <h2 className="text-base font-bold text-slate-800">
              {isEdit ? 'Edit Purchase Order' : 'New Purchase Order'}
            </h2>
            {isEdit && (
              <span className="ml-2 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-slate-100 text-slate-600">
                {po.poNumber}
              </span>
            )}
          </div>
          <button onClick={onClose} className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-slate-200 bg-white">
          {TABS.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-1.5 px-4 py-2.5 text-xs font-medium border-b-2 transition-colors ${
                  activeTab === tab.id
                    ? 'border-teal-500 text-teal-700 bg-teal-50/50'
                    : 'border-transparent text-slate-500 hover:text-slate-700 hover:bg-slate-50'
                }`}
              >
                <Icon className="w-3.5 h-3.5" />
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-4">

          {/* Common fields */}
          {(activeTab === 'manual' || activeTab === 'excel') && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-5">
              {/* Supplier */}
              <div className="relative" ref={supplierDropdownRef}>
                <label className="block text-[10px] uppercase tracking-wide font-semibold text-slate-500 mb-1">
                  Supplier <span className="text-red-400">*</span>
                </label>
                <button
                  type="button"
                  onClick={() => setShowSupplierDropdown(!showSupplierDropdown)}
                  className={`w-full flex items-center justify-between px-3 py-2 text-xs border rounded-lg transition-colors ${
                    errors.supplier ? 'border-red-300 bg-red-50' : 'border-slate-200 hover:border-slate-300'
                  }`}
                >
                  <span className={selectedSupplier ? 'text-slate-800' : 'text-slate-400'}>
                    {selectedSupplier?.name || 'Select supplier...'}
                  </span>
                  <ChevronDown className="w-3.5 h-3.5 text-slate-400" />
                </button>
                {errors.supplier && <p className="text-[10px] text-red-500 mt-0.5">{errors.supplier}</p>}
                {showSupplierDropdown && (
                  <div className="absolute left-0 top-full mt-1 w-full bg-white border border-slate-200 rounded-lg shadow-lg z-30 py-1">
                    <div className="px-2 pb-1">
                      <div className="relative">
                        <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-slate-400" />
                        <input
                          autoFocus
                          type="text"
                          placeholder="Search suppliers..."
                          value={supplierSearch}
                          onChange={(e) => setSupplierSearch(e.target.value)}
                          className="w-full pl-7 pr-2 py-1.5 text-xs border border-slate-200 rounded focus:outline-none focus:ring-1 focus:ring-teal-400"
                        />
                      </div>
                    </div>
                    <div className="max-h-40 overflow-y-auto">
                      {filteredSuppliers.length === 0 ? (
                        <p className="px-3 py-2 text-xs text-slate-400">No suppliers found</p>
                      ) : (
                        filteredSuppliers.map((s) => (
                          <button
                            key={s.id}
                            onClick={() => { setSupplierId(s.id); setShowSupplierDropdown(false); setSupplierSearch(''); }}
                            className={`w-full text-left px-3 py-2 text-xs hover:bg-slate-50 ${
                              Number(supplierId) === s.id ? 'bg-teal-50 text-teal-700 font-medium' : 'text-slate-700'
                            }`}
                          >
                            {s.name}
                          </button>
                        ))
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Order Date */}
              <div>
                <label className="block text-[10px] uppercase tracking-wide font-semibold text-slate-500 mb-1">Order Date</label>
                <input
                  type="date" value={orderDate}
                  onChange={(e) => setOrderDate(e.target.value)}
                  className="w-full px-3 py-2 text-xs border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-400"
                />
              </div>

              {/* Expected Delivery */}
              <div>
                <label className="block text-[10px] uppercase tracking-wide font-semibold text-slate-500 mb-1">Expected Delivery</label>
                <input
                  type="date" value={expectedDeliveryDate}
                  onChange={(e) => setExpectedDeliveryDate(e.target.value)}
                  className="w-full px-3 py-2 text-xs border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-400"
                />
              </div>

              {/* Notes */}
              <div>
                <label className="block text-[10px] uppercase tracking-wide font-semibold text-slate-500 mb-1">Notes</label>
                <input
                  type="text" value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Optional notes..."
                  className="w-full px-3 py-2 text-xs border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-400"
                />
              </div>
            </div>
          )}

          {/* ── Manual Entry Tab ── */}
          {activeTab === 'manual' && (
            <div>
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-xs font-semibold text-slate-700 uppercase tracking-wide">Line Items</h3>
                <div className="flex items-center gap-1">
                  <button onClick={openOtherCharges} className="flex items-center gap-1 px-2 py-1 text-xs text-slate-600 hover:bg-slate-100 rounded-lg transition-colors font-medium">
                    <Plus className="w-3.5 h-3.5" /> Add Other Charges
                  </button>
                  <button onClick={addLine} className="flex items-center gap-1 px-2 py-1 text-xs text-teal-600 hover:bg-teal-50 rounded-lg transition-colors font-medium">
                    <Plus className="w-3.5 h-3.5" /> Add Item
                  </button>
                </div>
              </div>
              {errors.items && <p className="text-[10px] text-red-500 mb-2">{errors.items}</p>}

              <div className="border border-slate-200 rounded-lg overflow-x-auto">
                <table className="w-full text-xs min-w-[720px]">
                  <thead>
                    <tr className="bg-slate-50 border-b border-slate-200">
                      <th className="text-left   px-3 py-2 font-semibold text-slate-600 w-[24%] rounded-tl-lg">Item</th>
                      <th className="text-center  px-3 py-2 font-semibold text-slate-600 w-[9%]">Qty</th>
                      <th className="text-center  px-3 py-2 font-semibold text-slate-600 w-[8%]">UOM</th>
                      <th className="text-right   px-3 py-2 font-semibold text-slate-600 w-[15%]">Unit Price (₹)</th>
                      <th className="text-left    px-3 py-2 font-semibold text-slate-600 w-[21%]">Tax</th>
                      <th className="text-right   px-3 py-2 font-semibold text-slate-600 w-[16%]">Total (₹)</th>
                      <th className="w-[7%] rounded-tr-lg"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {lineItems.map((li, idx) => (
                      <tr key={li._key} className="hover:bg-slate-50/40">
                        {/* Item selector */}
                        <td className="px-3 py-2" ref={openItemDropdown === idx ? itemDropdownRef : null}>
                          <button
                            type="button"
                            onClick={(e) => {
                              if (openItemDropdown === idx) {
                                setOpenItemDropdown(null); setDropdownPos(null);
                              } else {
                                const rect = e.currentTarget.getBoundingClientRect();
                                setDropdownPos({ top: rect.bottom + 2, left: rect.left, width: rect.width });
                                setOpenItemDropdown(idx);
                                setItemSearch('');
                              }
                            }}
                            className={`w-full text-left px-2 py-1.5 border rounded text-xs ${
                              errors[`item_${idx}`] ? 'border-red-300 bg-red-50' : 'border-slate-200'
                            }`}
                          >
                            {li.itemName || <span className="text-slate-400">Select item...</span>}
                          </button>
                          {openItemDropdown === idx && dropdownPos && (
                            <div
                              className="fixed bg-white border border-slate-200 rounded-lg shadow-xl z-[100] py-1"
                              style={{ top: dropdownPos.top, left: dropdownPos.left, width: dropdownPos.width }}
                            >
                              <div className="px-2 pb-1">
                                <div className="relative">
                                  <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-slate-400" />
                                  <input
                                    autoFocus
                                    type="text"
                                    placeholder="Search items..."
                                    value={itemSearch}
                                    onChange={(e) => setItemSearch(e.target.value)}
                                    className="w-full pl-7 pr-2 py-1.5 text-xs border border-slate-200 rounded focus:outline-none focus:ring-1 focus:ring-teal-400"
                                  />
                                </div>
                              </div>
                              <div className="max-h-48 overflow-y-auto">
                                {loadingItems ? (
                                  <div className="flex items-center justify-center py-3">
                                    <Loader2 className="w-4 h-4 text-teal-500 animate-spin" />
                                  </div>
                                ) : filteredInventoryItems.length === 0 ? (
                                  <p className="px-3 py-2 text-xs text-slate-400">No items found</p>
                                ) : (
                                  filteredInventoryItems.map((item) => (
                                    <button
                                      key={item.id}
                                      onClick={() => selectItem(idx, item)}
                                      className="w-full text-left px-3 py-1.5 text-xs hover:bg-slate-50 text-slate-700"
                                    >
                                      {item.name} <span className="text-slate-400">({item.uom})</span>
                                    </button>
                                  ))
                                )}
                              </div>
                            </div>
                          )}
                        </td>
                        {/* Quantity */}
                        <td className="px-3 py-2">
                          <input
                            type="number" min="0" step="0.01"
                            value={li.quantity}
                            onChange={(e) => updateLine(idx, 'quantity', e.target.value)}
                            onWheel={preventWheelChange}
                            className={`w-full text-center px-2 py-1.5 border rounded text-xs ${
                              errors[`qty_${idx}`] ? 'border-red-300 bg-red-50' : 'border-slate-200'
                            } focus:outline-none focus:ring-1 focus:ring-teal-400`}
                          />
                        </td>
                        {/* UOM */}
                        <td className="px-3 py-2 text-center text-slate-500">{li.uom || '—'}</td>
                        {/* Unit Price */}
                        <td className="px-3 py-2">
                          <input
                            type="number" min="0" step="0.01"
                            value={li.unitPrice}
                            onChange={(e) => updateLine(idx, 'unitPrice', e.target.value)}
                            onWheel={preventWheelChange}
                            className={`w-full text-right px-2 py-1.5 border rounded text-xs ${
                              errors[`price_${idx}`] ? 'border-red-300 bg-red-50' : 'border-slate-200'
                            } focus:outline-none focus:ring-1 focus:ring-teal-400`}
                          />
                        </td>
                        {/* Tax (same API/hook as Menu items) */}
                        <td className="px-3 py-2">
                          <select
                            value={li.taxId ?? ''}
                            onChange={(e) => updateLine(idx, 'taxId', e.target.value ? Number(e.target.value) : null)}
                            className="w-full px-2 py-1.5 border border-slate-200 rounded text-xs bg-white focus:outline-none focus:ring-1 focus:ring-teal-400"
                          >
                            <option value="">No tax</option>
                            {activeTaxes.map((tax) => (
                              <option key={tax.id} value={tax.id}>
                                {tax.name} ({tax.rate}%)
                              </option>
                            ))}
                          </select>
                        </td>
                        {/* Total (incl. tax) */}
                        <td className="px-3 py-2 text-right font-medium text-slate-700">
                          ₹{(lineRows[idx]?.total || 0).toLocaleString('en-IN')}
                        </td>
                        {/* Remove */}
                        <td className="px-3 py-2 text-center">
                          {lineItems.length > 1 && (
                            <button onClick={() => removeLine(idx)} className="p-1 text-slate-400 hover:text-red-500 rounded transition-colors">
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* ── Other Charges (dynamic) ── */}
              {showOtherCharges && (
                <div className="mt-4 border border-slate-200 rounded-lg p-4 bg-slate-50/60">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-xs font-semibold text-slate-700 uppercase tracking-wide">Other Charges</h3>
                    <button
                      onClick={addCharge}
                      className="flex items-center gap-1 px-2 py-1 text-xs text-teal-600 hover:bg-teal-50 rounded-lg transition-colors font-medium"
                    >
                      <Plus className="w-3.5 h-3.5" /> Add Charge
                    </button>
                  </div>

                  {/* Column labels */}
                  <div className="flex items-center gap-3 px-0.5 pb-2.5 text-[10px] uppercase tracking-wide font-semibold text-slate-600">
                    <span className="flex-1">Description</span>
                    <span className="w-36 text-right pr-2">Amount (₹)</span>
                    <span className="w-44">Tax</span>
                    <span className="w-28 text-right pr-2">Total (₹)</span>
                    <span className="w-8" />
                  </div>

                  <div className="space-y-2.5">
                    {otherCharges.map((c, idx) => (
                      <div key={c._key} className="flex items-center gap-3">
                        {/* Description */}
                        <input
                          type="text"
                          value={c.name}
                          onChange={(e) => updateCharge(idx, 'name', e.target.value)}
                          placeholder="e.g., Delivery, Packing"
                          className="flex-1 min-w-0 h-9 px-3 text-xs border border-slate-200 rounded-lg bg-white focus:outline-none focus:ring-1 focus:ring-teal-400 focus:border-teal-400"
                        />
                        {/* Amount (pre-tax) */}
                        <div className="flex items-center h-9 w-36 border border-slate-200 rounded-lg bg-white px-2.5 focus-within:ring-1 focus-within:ring-teal-400 focus-within:border-teal-400">
                          <span className="text-slate-400 text-xs mr-1.5">₹</span>
                          <input
                            type="number" min="0" step="0.01"
                            value={c.amount}
                            onChange={(e) => updateCharge(idx, 'amount', e.target.value)}
                            onWheel={preventWheelChange}
                            className="w-full text-right bg-transparent text-xs focus:outline-none"
                          />
                        </div>
                        {/* Tax (defaults to No Tax; same API/hook as item tax) */}
                        <select
                          value={c.taxId ?? ''}
                          onChange={(e) => updateCharge(idx, 'taxId', e.target.value ? Number(e.target.value) : null)}
                          className="w-44 h-9 px-2.5 text-xs border border-slate-200 rounded-lg bg-white focus:outline-none focus:ring-1 focus:ring-teal-400 focus:border-teal-400"
                        >
                          <option value="">No tax</option>
                          {activeTaxes.map((tax) => (
                            <option key={tax.id} value={tax.id}>
                              {tax.name} ({tax.rate}%)
                            </option>
                          ))}
                        </select>
                        {/* Total (incl. tax) — read-only */}
                        <div className="w-28 text-right pr-2 text-xs font-semibold text-slate-700 tabular-nums">
                          ₹{(chargeRows[idx]?.total || 0).toLocaleString('en-IN')}
                        </div>
                        {/* Remove */}
                        <button
                          onClick={() => removeCharge(idx)}
                          className="w-8 h-9 flex items-center justify-center text-slate-400 hover:text-red-500 rounded transition-colors"
                          title="Remove charge"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    ))}
                  </div>

                  {/* Total — aligned with the Total column */}
                  <div className="flex items-center gap-3 mt-3 pt-3 border-t border-slate-200">
                    <span className="flex-1 text-right text-xs font-semibold text-slate-600">Total Charges (incl. tax)</span>
                    <span className="w-28 text-right pr-2 text-sm font-bold text-slate-800 tabular-nums">₹{extraCharges.toLocaleString('en-IN')}</span>
                    <span className="w-8" />
                  </div>
                </div>
              )}

              {/* Pricing Summary */}
              <div className="mt-4">
                <div className="w-full rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden">
                  <div className="px-4 py-3.5 space-y-2.5">
                    {/* Subtotal */}
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-slate-500">Subtotal</span>
                      <span className="font-medium text-slate-700 tabular-nums">₹{subtotal.toLocaleString('en-IN')}</span>
                    </div>

                    {/* Discount (editable — Flat ₹ or Percentage %) */}
                    <div className="flex items-center justify-between text-xs gap-2">
                      <span className="text-slate-500">Discount</span>
                      <div className="flex items-center gap-1.5">
                        {/* Type toggle */}
                        <div className="flex border border-slate-200 rounded-lg overflow-hidden">
                          <button
                            type="button"
                            onClick={() => setDiscountType('FLAT')}
                            className={`px-2 py-1 text-xs font-semibold transition-colors ${
                              discountType === 'FLAT' ? 'bg-teal-600 text-white' : 'bg-white text-slate-500 hover:bg-slate-50'
                            }`}
                          >
                            ₹
                          </button>
                          <button
                            type="button"
                            onClick={() => setDiscountType('PERCENT')}
                            className={`px-2 py-1 text-xs font-semibold border-l border-slate-200 transition-colors ${
                              discountType === 'PERCENT' ? 'bg-teal-600 text-white' : 'bg-white text-slate-500 hover:bg-slate-50'
                            }`}
                          >
                            %
                          </button>
                        </div>
                        {/* Value */}
                        <div className="flex items-center border border-slate-200 rounded-lg bg-white px-2 py-1 w-24 focus-within:ring-1 focus-within:ring-teal-400 focus-within:border-teal-400">
                          <span className="text-slate-400 mr-1">{discountType === 'PERCENT' ? '%' : '₹'}</span>
                          <input
                            type="number" min="0" step="0.01"
                            max={discountType === 'PERCENT' ? 100 : undefined}
                            value={discount}
                            onChange={(e) => setDiscount(e.target.value)}
                            onWheel={preventWheelChange}
                            className="w-full text-right bg-transparent text-xs font-medium text-emerald-600 tabular-nums focus:outline-none"
                          />
                        </div>
                      </div>
                    </div>
                    {/* Resolved amount when discount is a percentage */}
                    {discountType === 'PERCENT' && discountAmount > 0 && (
                      <div className="flex items-center justify-end text-[11px] text-slate-400 -mt-1.5">
                        − ₹{discountAmount.toLocaleString('en-IN')} applied
                      </div>
                    )}

                    {/* Item Tax (exclusive, items only — charge tax sits inside Other Charges) */}
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-slate-500">Tax</span>
                      <span className="font-medium text-slate-700 tabular-nums">₹{itemTaxTotal.toLocaleString('en-IN')}</span>
                    </div>

                    {/* Other Charges (incl. tax) */}
                    {extraCharges > 0 && (
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-slate-500">Other Charges (incl. tax)</span>
                        <span className="font-medium text-slate-700 tabular-nums">+ ₹{extraCharges.toLocaleString('en-IN')}</span>
                      </div>
                    )}

                    {/* Charge Breakdown — each charge's total incl. tax */}
                    {namedCharges.length > 0 && (
                      <div className="rounded-lg bg-slate-50 border border-slate-100 px-3 py-2.5 space-y-1.5">
                        <p className="text-[9px] uppercase tracking-wider font-semibold text-slate-400">Charge Breakdown</p>
                        {namedCharges.map((c) => (
                          <div key={c._key} className="flex items-center justify-between gap-2">
                            <div className="flex items-center gap-1.5 min-w-0">
                              <span className="w-1 h-1 rounded-full bg-slate-300 flex-shrink-0" />
                              <span className="text-[11px] text-slate-600 truncate">{c.name.trim() || 'Unnamed charge'}</span>
                            </div>
                            <span className="text-[11px] font-medium text-slate-600 tabular-nums whitespace-nowrap">₹{c.total.toLocaleString('en-IN')}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Grand Total */}
                  <div className="flex items-center justify-between px-4 py-3 bg-teal-50 border-t border-teal-100">
                    <span className="text-sm font-semibold text-teal-800">Grand Total</span>
                    <span className="text-base font-bold text-teal-700 tabular-nums">₹{grandTotal.toLocaleString('en-IN')}</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ── Excel Upload Tab ── */}
          {activeTab === 'excel' && (
            <div>
              {!excelFile ? (
                <div
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={handleExcelDrop}
                  onClick={() => excelInputRef.current?.click()}
                  className="border-2 border-dashed border-slate-300 rounded-xl p-10 text-center hover:border-teal-400 hover:bg-teal-50/30 transition-colors cursor-pointer"
                >
                  <Upload className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                  <p className="text-sm font-medium text-slate-600 mb-1">Drag & drop your Excel file here</p>
                  <p className="text-xs text-slate-400 mb-3">Supports .xlsx, .xls, .csv (max 5MB)</p>
                  <button type="button" className="px-4 py-2 bg-teal-600 text-white text-xs font-semibold rounded-lg hover:bg-teal-700 transition-colors">
                    Browse Files
                  </button>
                  <input ref={excelInputRef} type="file" accept=".xlsx,.xls,.csv" onChange={handleExcelDrop} className="hidden" />
                </div>
              ) : (
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <FileSpreadsheet className="w-4 h-4 text-green-600" />
                      <span className="text-xs font-medium text-slate-700">{excelFile.name}</span>
                    </div>
                    <button onClick={() => { setExcelFile(null); setExcelParsed(null); }} className="text-xs text-red-500 hover:text-red-700">
                      Remove
                    </button>
                  </div>
                  {uploadingExcel ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="w-5 h-5 text-teal-500 animate-spin mr-2" />
                      <span className="text-sm text-slate-500">Parsing file...</span>
                    </div>
                  ) : excelParsed ? (
                    <>
                      <div className="border border-slate-200 rounded-lg overflow-hidden mb-3">
                        <table className="w-full text-xs">
                          <thead>
                            <tr className="bg-slate-50 border-b border-slate-200">
                              <th className="text-left  px-3 py-2 font-semibold text-slate-600">Item Name</th>
                              <th className="text-center px-3 py-2 font-semibold text-slate-600">Qty</th>
                              <th className="text-center px-3 py-2 font-semibold text-slate-600">UOM</th>
                              <th className="text-right  px-3 py-2 font-semibold text-slate-600">Unit Price (₹)</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-slate-100">
                            {excelParsed.map((item, idx) => (
                              <tr key={idx} className="hover:bg-slate-50/40">
                                <td className="px-3 py-2 text-slate-700">{item.itemName ?? item.name}</td>
                                <td className="px-3 py-2 text-center text-slate-600">{item.quantity}</td>
                                <td className="px-3 py-2 text-center text-slate-500">{item.uom}</td>
                                <td className="px-3 py-2 text-right text-slate-700">₹{(item.unitPrice || 0).toLocaleString('en-IN')}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      <button
                        onClick={importExcelItems}
                        className="flex items-center gap-1.5 px-4 py-2 bg-teal-600 text-white text-xs font-semibold rounded-lg hover:bg-teal-700 transition-colors"
                      >
                        <Download className="w-3.5 h-3.5" /> Import Items
                      </button>
                    </>
                  ) : null}
                </div>
              )}
              <div className="mt-4">
                <a
                  href="#"
                  onClick={(e) => { e.preventDefault(); toast('Template download coming soon'); }}
                  className="text-xs text-teal-600 hover:text-teal-700 underline"
                >
                  Download Excel Template
                </a>
              </div>
            </div>
          )}

          {/* ── Image / PDF AI Extraction Tab ── */}
          {activeTab === 'image' && (
            <div>
              <div className="flex items-start gap-2 p-3 bg-blue-50 border border-blue-200 rounded-lg mb-4">
                <Sparkles className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-xs font-semibold text-blue-700">AI Extraction</p>
                  <p className="text-[11px] text-blue-600 mt-0.5">
                    Upload a supplier invoice, quotation, or order list (image or PDF). We&apos;ll read it
                    and pre-fill the line items — you review, map them to inventory, and submit.
                  </p>
                </div>
              </div>

              {!imageFile ? (
                <div
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={handleImageDrop}
                  onClick={() => imageInputRef.current?.click()}
                  className="border-2 border-dashed border-slate-300 rounded-xl p-10 text-center hover:border-teal-400 hover:bg-teal-50/30 transition-colors cursor-pointer"
                >
                  <Image className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                  <p className="text-sm font-medium text-slate-600 mb-1">Drag &amp; drop your invoice here</p>
                  <p className="text-xs text-slate-400 mb-3">Supports .jpg, .png, .pdf (max 10MB)</p>
                  <button type="button" className="px-4 py-2 bg-teal-600 text-white text-xs font-semibold rounded-lg hover:bg-teal-700 transition-colors">
                    Browse Files
                  </button>
                  <input ref={imageInputRef} type="file" accept=".jpg,.jpeg,.png,.pdf" onChange={handleImageDrop} className="hidden" />
                </div>
              ) : (
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <Image className="w-4 h-4 text-blue-600" />
                      <span className="text-xs font-medium text-slate-700">{imageFile.name}</span>
                    </div>
                    <button onClick={clearImage} className="text-xs text-red-500 hover:text-red-700">
                      Remove
                    </button>
                  </div>

                  {imagePreview && (
                    <div className="border border-slate-200 rounded-lg overflow-hidden mb-3 max-h-60">
                      <img src={imagePreview} alt="Invoice preview" className="w-full h-full object-contain" />
                    </div>
                  )}

                  {!extraction && (
                    <button
                      onClick={handleExtract}
                      disabled={extracting}
                      className="flex items-center gap-1.5 px-4 py-2 bg-teal-600 text-white text-xs font-semibold rounded-lg hover:bg-teal-700 transition-colors disabled:opacity-50"
                    >
                      {extracting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
                      {extracting ? 'Reading document…' : 'Extract with AI'}
                    </button>
                  )}

                  {/* Review & map */}
                  {extraction && (
                    <div className="mt-4 space-y-4">
                      {/* Supplier */}
                      <div>
                        <label className="block text-[10px] uppercase tracking-wide font-semibold text-slate-500 mb-1">
                          Supplier
                        </label>
                        <select
                          value={reviewSupplierId}
                          onChange={(e) => setReviewSupplierId(e.target.value)}
                          className="w-full px-3 py-2 text-xs border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500/20 focus:border-teal-400"
                        >
                          <option value="">— Select later —</option>
                          {(extraction.supplierMatches || []).map((m) => (
                            <option key={m.id} value={m.id}>
                              {m.name} ({Math.round((m.score || 0) * 100)}%)
                            </option>
                          ))}
                        </select>
                        {extraction.supplierName && (
                          <p className="text-[10px] text-slate-400 mt-0.5">
                            Read from document: “{extraction.supplierName}”
                            {extraction.supplierGstin ? ` · GSTIN ${extraction.supplierGstin}` : ''}
                          </p>
                        )}
                      </div>

                      {/* Items */}
                      <div>
                        <h3 className="text-xs font-semibold text-slate-700 uppercase tracking-wide mb-2">
                          Extracted Items ({reviewItems.length})
                        </h3>
                        <div className="border border-slate-200 rounded-lg overflow-x-auto">
                          <table className="w-full text-xs">
                            <thead>
                              <tr className="bg-slate-50 border-b border-slate-200">
                                <th className="text-left   px-2 py-2 font-semibold text-slate-600 w-[24%]">Item (read)</th>
                                <th className="text-center px-2 py-2 font-semibold text-slate-600 w-[10%]">Qty</th>
                                <th className="text-center px-2 py-2 font-semibold text-slate-600 w-[10%]">UOM</th>
                                <th className="text-right  px-2 py-2 font-semibold text-slate-600 w-[14%]">Price (₹)</th>
                                <th className="text-left   px-2 py-2 font-semibold text-slate-600 w-[30%]">Map to inventory</th>
                                <th className="text-center px-2 py-2 font-semibold text-slate-600 w-[12%]">Match</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                              {reviewItems.map((ri, idx) => (
                                <tr key={ri._key} className="hover:bg-slate-50/40">
                                  <td className="px-2 py-1.5">
                                    <input
                                      type="text"
                                      value={ri.itemName}
                                      onChange={(e) => updateReviewItem(idx, 'itemName', e.target.value)}
                                      className="w-full px-2 py-1 border border-slate-200 rounded text-xs focus:outline-none focus:ring-1 focus:ring-teal-400"
                                    />
                                  </td>
                                  <td className="px-2 py-1.5">
                                    <input
                                      type="number" min="0" step="0.01"
                                      value={ri.quantity}
                                      onChange={(e) => updateReviewItem(idx, 'quantity', e.target.value)}
                                      onWheel={preventWheelChange}
                                      className="w-full text-center px-1 py-1 border border-slate-200 rounded text-xs focus:outline-none focus:ring-1 focus:ring-teal-400"
                                    />
                                  </td>
                                  <td className="px-2 py-1.5">
                                    <input
                                      type="text"
                                      value={ri.uom}
                                      onChange={(e) => updateReviewItem(idx, 'uom', e.target.value)}
                                      className="w-full text-center px-1 py-1 border border-slate-200 rounded text-xs focus:outline-none focus:ring-1 focus:ring-teal-400"
                                      placeholder="—"
                                    />
                                  </td>
                                  <td className="px-2 py-1.5">
                                    <input
                                      type="number" min="0" step="0.01"
                                      value={ri.unitPrice}
                                      onChange={(e) => updateReviewItem(idx, 'unitPrice', e.target.value)}
                                      onWheel={preventWheelChange}
                                      className="w-full text-right px-1 py-1 border border-slate-200 rounded text-xs focus:outline-none focus:ring-1 focus:ring-teal-400"
                                    />
                                  </td>
                                  <td className="px-2 py-1.5">
                                    <select
                                      value={ri.chosenInventoryItemId}
                                      onChange={(e) => updateReviewItem(idx, 'chosenInventoryItemId', e.target.value)}
                                      className="w-full px-2 py-1 border border-slate-200 rounded text-xs focus:outline-none focus:ring-1 focus:ring-teal-400"
                                    >
                                      <option value="">— No match (add as text) —</option>
                                      {(ri.matches || []).map((m) => (
                                        <option key={m.id} value={m.id}>
                                          {m.name} ({Math.round((m.score || 0) * 100)}%)
                                        </option>
                                      ))}
                                    </select>
                                  </td>
                                  <td className="px-2 py-1.5 text-center">
                                    <span className={`px-1.5 py-0.5 rounded-full text-[9px] font-semibold ${CONFIDENCE_STYLES[ri.matchConfidence] || CONFIDENCE_STYLES.NONE}`}>
                                      {ri.matchConfidence}
                                    </span>
                                  </td>
                                </tr>
                              ))}
                              {reviewItems.length === 0 && (
                                <tr>
                                  <td colSpan={6} className="px-3 py-4 text-center text-slate-400 text-xs">
                                    No items detected. Switch to Manual Entry to add them yourself.
                                  </td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </div>
                      </div>

                      {extraction.notes && (
                        <div className="flex items-start gap-2 p-2.5 bg-amber-50 border border-amber-200 rounded-lg">
                          <AlertTriangle className="w-3.5 h-3.5 text-amber-500 mt-0.5 flex-shrink-0" />
                          <p className="text-[11px] text-amber-700">{extraction.notes}</p>
                        </div>
                      )}

                      <button
                        onClick={importExtracted}
                        disabled={reviewItems.length === 0}
                        className="flex items-center gap-1.5 px-4 py-2 bg-teal-600 text-white text-xs font-semibold rounded-lg hover:bg-teal-700 transition-colors disabled:opacity-50"
                      >
                        <CheckCircle2 className="w-3.5 h-3.5" />
                        Import {reviewItems.length} item(s) to form
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 px-6 py-3 border-t border-slate-200 bg-slate-50">
          <button onClick={onClose} className="px-4 py-2 text-xs font-medium text-slate-600 border border-slate-200 rounded-lg hover:bg-slate-100 transition-colors">
            Cancel
          </button>
          <button
            onClick={() => handleSave('DRAFT')}
            disabled={saving}
            className="flex items-center gap-1.5 px-4 py-2 text-xs font-semibold border border-teal-200 text-teal-700 rounded-lg hover:bg-teal-50 transition-colors disabled:opacity-50"
          >
            {saving && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
            Save as Draft
          </button>
          <button
            onClick={() => handleSave('PENDING')}
            disabled={saving}
            className="flex items-center gap-1.5 px-4 py-2 bg-teal-600 text-white text-xs font-semibold rounded-lg hover:bg-teal-700 transition-colors disabled:opacity-50"
          >
            {saving && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
            Submit
          </button>
        </div>
      </div>
    </div>
  );
}